import * as React from "react";
import { Box, Grid2 as Grid, Skeleton, Typography, Tooltip } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

export type KPIItem = {
  label: string;
  value: number | string;
  link?: string;
  tooltip?: string;
};

type KPIStatCardProps = {
  items: KPIItem[];
  loading?: boolean;
  skeletonCount?: number;
  emphasizeFirst?: boolean;
  valueFormatter?: (v: KPIItem["value"]) => string;
  columns?: number; // This now defines the fixed grid - all stats get equal width based on this number
};

export const KPIStatCard: React.FC<KPIStatCardProps> = ({
  items,
  loading = false,
  skeletonCount = 5,
  emphasizeFirst = false,
  valueFormatter,
  columns = 5, // Default to 5-column fixed grid
}) => {
  const colSpan = 12 / columns; // Fixed grid width per stat
  const fmt = (v: KPIItem["value"]) => (typeof v === "number" ? v.toLocaleString() : String(v));

  if (loading) {
    return (
      <Grid container spacing={0}>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <Grid
            key={i}
            size={{
              xs: 12 / columns,
              md: colSpan,
            }}
          >
            <Box
              sx={{
                width: "100%",
                pl: 3,
                pr: 2,
                py: 1.25,
                boxSizing: "border-box",
                ...(i > 0 && {
                  borderLeft: "1px solid",
                  borderColor: "divider",
                }),
              }}
            >
              <Skeleton variant="text" width={80} height={36} />
              <Skeleton variant="text" width={140} height={20} />
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={0}>
      {items.map((s, i) => {
        const content = (
          <Box
            component={s.link ? RouterLink : "div"}
            to={s.link || ""}
            sx={{
              display: "block",
              width: "100%",
              height: "100%",
              pl: 3,
              pr: 2,
              py: 1.25,
              textDecoration: "none",
              color: "inherit",
              boxSizing: "border-box",
              ...(i === 0 && emphasizeFirst
                ? {
                    borderLeft: "3px solid",
                    borderColor: "text.primary",
                  }
                : i > 0
                  ? {
                      borderLeft: "1px solid",
                      borderColor: "divider",
                    }
                  : {}),
              "&:hover .kpiLabel": {
                textDecoration: s.link ? "underline" : "none",
              },
              outline: "none",
              "&:focus-visible": {
                boxShadow: (t) => `0 0 0 2px ${t.palette.action.focus}`,
                borderRadius: 1,
              },
            }}
            aria-label={`${s.label}: ${typeof s.value === "number" ? s.value.toLocaleString() : s.value}`}
          >
            <Typography variant="h2" fontWeight={800} sx={{ lineHeight: 1 }}>
              {(valueFormatter ?? fmt)(s.value)}
            </Typography>
            <Typography className="kpiLabel" variant="body2" color="text.secondary" noWrap>
              {s.label}
            </Typography>
          </Box>
        );

        return (
          <Grid
            key={s.label}
            size={{
              xs: 12 / columns,
              md: colSpan,
            }}
          >
            {s.tooltip ? <Tooltip title={s.tooltip}>{content}</Tooltip> : content}
          </Grid>
        );
      })}
    </Grid>
  );
};
